import React, { useState } from "react";
import PropTypes from "prop-types";

import Money from "../../components/Money";
import { useHouseDispatch } from "../../contexts/HouseContext";
import { MethodGlyph, paymentLabel, PAYMENT_METHODS } from "./components/paymentMethods";
import { outstandingBuyIns } from "./mobile/playerGroups";
import "./CashOutFlow.css";

/**
 * Guided cash-out for a single player. Walks three steps:
 *   1. Settle — toggle the settled state of the player's buy-ins.
 *   2. Count — enter the player's final chip stack.
 *   3. Review — preview the buy-ins the stack covers, then the payout split
 *      across the player's buy-in methods (digital first, then cash), each
 *      with a settled toggle, and submit.
 *
 * On submit the dispatch creates a cover transaction for each covered
 * buy-in plus one CASH_OUT per payout method. Rendered as a full-screen
 * sheet over the mobile ledger.
 */
export function CashOutFlow({ game, player, onClose }) {
    const { toggleTransactionStatus, cashOutPlayer } = useHouseDispatch();

    const buyIns = (player?.transactions ?? []).filter((t) => t.type === "BUY_IN");

    // Skip the settle step entirely when there are no buy-ins to settle.
    const [flow] = useState(() =>
        buyIns.length > 0 ? ["settle", "count", "review"] : ["count", "review"],
    );
    const [stepKey, setStepKey] = useState(flow[0]);
    const [stack, setStack] = useState("");
    const [methodOverrides, setMethodOverrides] = useState({});
    const [settledOverrides, setSettledOverrides] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const idx = flow.indexOf(stepKey);
    const stackNum = parseFloat(stack);
    const stackValid = isValidStack(stack);

    // Pay buy-ins down with the stack, oldest first — partial covers allowed.
    // Each cover is { transaction, amount }; the buy-in stays on the books.
    const outstanding = outstandingBuyIns(player).filter((b) => b.owed > 0);
    const covers = [];
    let stackLeft = stackValid ? stackNum : 0;
    for (const b of outstanding) {
        if (stackLeft <= 0) break;
        const amount = Math.min(b.owed, stackLeft);
        covers.push({ transaction: b.transaction, amount });
        stackLeft -= amount;
    }
    const coveredTotal = covers.reduce((s, c) => s + c.amount, 0);
    const payout = stackValid ? Math.round((stackNum - coveredTotal) * 100) / 100 : 0;

    // Split the payout across the player's settled buy-in methods, paying
    // digital methods back first (up to what they bought in that way), then
    // cash for the remainder — one CASH_OUT per method. The host can then
    // change a payout's method or settled state before submitting; overrides
    // are keyed by row index so changing the method doesn't move the key.
    const payouts = resolvePayouts(
        computePayouts(player, payout),
        methodOverrides,
        settledOverrides,
    );
    const setPayoutMethod = (i, type) =>
        setMethodOverrides((o) => ({ ...o, [i]: type }));
    const togglePayoutSettled = (i) =>
        setSettledOverrides((o) => ({ ...o, [i]: !payouts[i].isSettled }));

    // What the player still owes after this cash-out's covers — the host
    // settles these later as the money comes in.
    const coverByTxn = new Map(covers.map((c) => [c.transaction.id, c.amount]));
    const stillOwed = outstanding
        .map((b) => ({
            transaction: b.transaction,
            owed: Math.round((b.owed - (coverByTxn.get(b.transaction.id) ?? 0)) * 100) / 100,
        }))
        .filter((b) => b.owed > 0);

    const goNext = () => setStepKey(flow[Math.min(idx + 1, flow.length - 1)]);
    const goBack = () => setStepKey(flow[Math.max(idx - 1, 0)]);

    const toggleBuyIn = (t) =>
        toggleTransactionStatus(game.id, player.id, { ...t, isSettled: !t.isSettled });

    const handleSubmit = async () => {
        setSubmitting(true);
        setError(null);
        const result = await cashOutPlayer(game.id, player.id, {
            covers,
            payouts,
        });
        setSubmitting(false);
        if (result.error) {
            setError(result.error);
            return;
        }
        onClose();
    };

    if (!player) return null;

    return (
        <div className="co" role="dialog" aria-label={`Cash out ${player.name}`}>
            <header className="co-head">
                <button type="button" className="co-close" onClick={onClose} aria-label="Cancel">
                    ×
                </button>
                <div className="co-kicker">Cash out</div>
                <h1 className="co-title">{player.name}</h1>
                <ol className="co-steps">
                    {flow.map((key, i) => (
                        <li
                            key={key}
                            className={`co-step${i === idx ? " is-on" : ""}${i < idx ? " is-done" : ""}`}
                        >
                            {STEP_LABELS[key]}
                        </li>
                    ))}
                </ol>
            </header>

            <div className="co-body">
                {stepKey === "settle" && (
                    <SettleStep buyIns={buyIns} onToggle={toggleBuyIn} />
                )}

                {stepKey === "count" && (
                    <CountStep stack={stack} onChange={setStack} />
                )}

                {stepKey === "review" && (
                    <ReviewStep
                        covers={covers}
                        coveredTotal={coveredTotal}
                        payout={payout}
                        stack={stackNum}
                        stillOwed={stillOwed}
                        payouts={payouts}
                        onChangeMethod={setPayoutMethod}
                        onToggleSettled={togglePayoutSettled}
                    />
                )}

                {error && <div className="co-error">{error}</div>}
            </div>

            <footer className="co-foot">
                {idx > 0 ? (
                    <button type="button" className="co-btn ghost" onClick={goBack}>
                        Back
                    </button>
                ) : (
                    <button type="button" className="co-btn ghost" onClick={onClose}>
                        Cancel
                    </button>
                )}
                {stepKey === "review" ? (
                    <button
                        type="button"
                        className="co-btn primary"
                        onClick={handleSubmit}
                        disabled={!stackValid || submitting}
                    >
                        {submitting ? "Cashing out…" : "Cash out"}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="co-btn primary"
                        onClick={goNext}
                        disabled={stepKey === "count" && !stackValid}
                    >
                        Next
                    </button>
                )}
            </footer>
        </div>
    );
}

const STEP_LABELS = { settle: "Settle", count: "Count", review: "Review" };

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Split a payout across the player's settled buy-in methods. Digital
 * methods (anything but cash) are reimbursed first, each capped at what the
 * player bought in that way, in buy-in order; whatever's left goes to cash.
 * Returns [{ paymentType, amount }] — one CASH_OUT per method. A zero
 * payout still yields a single $0 cash entry so the cash-out is recorded.
 */
// A valid stack is a non-negative number. $0 is allowed — a player can leave
// with no money and still close out their session; an empty field is not.
export function isValidStack(stack) {
    const n = parseFloat(stack);
    return !Number.isNaN(n) && n >= 0;
}

export function computePayouts(player, payout) {
    if (!(payout > 0)) return [{ paymentType: "CASH", amount: 0 }];

    const settledByMethod = new Map();
    for (const t of player?.transactions ?? []) {
        if (t.type === "BUY_IN" && t.isSettled) {
            settledByMethod.set(
                t.paymentType,
                (settledByMethod.get(t.paymentType) ?? 0) + (t.amount ?? 0),
            );
        }
    }

    const payouts = [];
    let remaining = payout;
    for (const [method, total] of settledByMethod) {
        if (remaining <= 0) break;
        if (method === "CASH") continue;
        const amount = round2(Math.min(remaining, total));
        if (amount > 0) {
            payouts.push({ paymentType: method, amount });
            remaining = round2(remaining - amount);
        }
    }
    if (remaining > 0 || payouts.length === 0) {
        payouts.push({ paymentType: "CASH", amount: round2(remaining) });
    }
    return payouts;
}

/**
 * Apply the host's per-row overrides to the computed payouts before submitting.
 * The host can change a payout's method and toggle its settled state; overrides
 * are keyed by row index so changing the method doesn't move the key. Settled
 * defaults to true for cash (handed over on the spot) and false for digital
 * methods (the payout still has to be sent) until the host overrides it.
 */
export function resolvePayouts(payouts, methodOverrides = {}, settledOverrides = {}) {
    return payouts.map((p, i) => {
        const paymentType = methodOverrides[i] ?? p.paymentType;
        const isSettled = settledOverrides[i] ?? paymentType === "CASH";
        return { paymentType, amount: p.amount, isSettled };
    });
}

function SettleStep({ buyIns, onToggle }) {
    return (
        <section className="co-section">
            <h2 className="co-h">Settle buy-ins</h2>
            <p className="co-note">
                Mark any buy-ins already paid. Whatever stays unsettled can be
                covered by the stack.
            </p>
            {buyIns.length === 0 ? (
                <div className="co-empty">No buy-ins on record.</div>
            ) : (
                <ul className="co-list">
                    {buyIns.map((t) => (
                        <li key={t.id} className="co-row">
                            <span className="co-row-glyph" aria-hidden="true">
                                <MethodGlyph type={t.paymentType} />
                            </span>
                            <span className="co-row-main">
                                <span className="co-row-amt">
                                    <Money amount={t.amount} />
                                </span>
                                <span className="co-row-sub">{paymentLabel(t.paymentType)}</span>
                            </span>
                            <button
                                type="button"
                                className={`co-state ${t.isSettled ? "is-settled" : "is-pending"}`}
                                onClick={() => onToggle(t)}
                                aria-pressed={t.isSettled}
                            >
                                {t.isSettled ? "Settled" : "Pending"}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function CountStep({ stack, onChange }) {
    return (
        <section className="co-section">
            <h2 className="co-h">Count the stack</h2>
            <p className="co-note">
                Enter the player&apos;s final chip count. We&apos;ll cover their
                unsettled buy-ins first, then pay out the rest.
            </p>
            <label className="co-amount">
                <span className="co-currency">$</span>
                <input
                    type="text"
                    inputMode="decimal"
                    className="co-amount-input"
                    placeholder="0"
                    value={stack}
                    onChange={(e) => onChange(e.target.value)}
                    autoFocus
                />
            </label>
        </section>
    );
}

// One payout row. The method is shown as a label; tapping its glyph reveals the
// method picker (mirrors the ledger transaction-detail row), and picking one
// applies it and closes the picker.
function PayoutRow({ payout, onChangeMethod, onToggleSettled }) {
    const [showPicker, setShowPicker] = useState(false);

    const pick = (type) => {
        setShowPicker(false);
        onChangeMethod(type);
    };

    return (
        <li className="co-row">
            <button
                type="button"
                className="co-row-glyph"
                onClick={() => setShowPicker((v) => !v)}
                aria-expanded={showPicker}
                aria-label={`Change payout method (currently ${paymentLabel(payout.paymentType)})`}
                title="Change method"
            >
                <MethodGlyph type={payout.paymentType} />
            </button>
            <span className="co-row-main">
                <span className="co-row-amt">
                    <Money amount={payout.amount} />
                </span>
                <span className="co-row-sub">{paymentLabel(payout.paymentType)}</span>
                {showPicker && (
                    <span className="co-method-picker" role="group" aria-label="Payout method">
                        {PAYMENT_METHODS.map((m) => (
                            <button
                                key={m.type}
                                type="button"
                                className={`co-method${m.type === payout.paymentType ? " is-on" : ""}`}
                                onClick={() => pick(m.type)}
                                aria-pressed={m.type === payout.paymentType}
                                aria-label={`Pay via ${m.label}`}
                                title={m.label}
                            >
                                <MethodGlyph type={m.type} />
                            </button>
                        ))}
                    </span>
                )}
            </span>
            <button
                type="button"
                className={`co-state ${payout.isSettled ? "is-settled" : "is-pending"}`}
                onClick={onToggleSettled}
                aria-pressed={payout.isSettled}
            >
                {payout.isSettled ? "Settled" : "Pending"}
            </button>
        </li>
    );
}

function ReviewStep({
    covers,
    coveredTotal,
    payout,
    stack,
    stillOwed,
    payouts,
    onChangeMethod,
    onToggleSettled,
}) {
    return (
        <section className="co-section">
            <h2 className="co-h">Review</h2>

            {covers.length > 0 && (
                <div className="co-block">
                    <div className="co-block-head">
                        <span>Covered by stack</span>
                        <span className="co-block-sum">
                            <Money amount={coveredTotal} />
                        </span>
                    </div>
                    <ul className="co-list">
                        {covers.map((c) => (
                            <li key={c.transaction.id} className="co-row">
                                <span className="co-row-glyph" aria-hidden="true">
                                    <MethodGlyph type={c.transaction.paymentType} />
                                </span>
                                <span className="co-row-main">
                                    <span className="co-row-amt">
                                        <Money amount={c.amount} />
                                    </span>
                                    <span className="co-row-sub">
                                        {paymentLabel(c.transaction.paymentType)} buy-in
                                        {c.amount < c.transaction.amount ? " (partial)" : ""}
                                    </span>
                                </span>
                                <span className="co-state is-settled">Covered</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {payout > 0 ? (
                <div className="co-block">
                    <div className="co-block-head">
                        <span>Pay out</span>
                        <span className="co-block-sum is-up">
                            <Money amount={payout} />
                        </span>
                    </div>
                    <p className="co-note">
                        Digital buy-ins are paid back first, then cash. Tap the
                        method icon to change how a payout is sent; tap the state
                        to settle one you&apos;ve already sent.
                    </p>
                    <ul className="co-list">
                        {payouts.map((p, i) => (
                            <PayoutRow
                                key={i}
                                payout={p}
                                onChangeMethod={(type) => onChangeMethod(i, type)}
                                onToggleSettled={() => onToggleSettled(i)}
                            />
                        ))}
                    </ul>
                </div>
            ) : (
                <div className="co-note co-note-flush">
                    The stack covers the buy-ins exactly — no cash payout.
                </div>
            )}

            <dl className="co-summary">
                <div>
                    <dt>Stack</dt>
                    <dd><Money amount={Number.isNaN(stack) ? 0 : stack} /></dd>
                </div>
                <div>
                    <dt>Covered</dt>
                    <dd><Money amount={coveredTotal} /></dd>
                </div>
                <div>
                    <dt>Payout</dt>
                    <dd><Money amount={payout} /></dd>
                </div>
            </dl>

            {stillOwed.length > 0 && (
                <div className="co-block co-owed">
                    <div className="co-block-head">
                        <span>Still owed</span>
                        <span className="co-block-sum is-warn">
                            <Money amount={stillOwed.reduce((s, b) => s + b.owed, 0)} />
                        </span>
                    </div>
                    <p className="co-note">
                        These buy-ins stay on the books — settle them after the
                        cash-out as the player pays.
                    </p>
                    <ul className="co-list">
                        {stillOwed.map((b) => (
                            <li key={b.transaction.id} className="co-row">
                                <span className="co-row-glyph" aria-hidden="true">
                                    <MethodGlyph type={b.transaction.paymentType} />
                                </span>
                                <span className="co-row-main">
                                    <span className="co-row-amt">
                                        <Money amount={b.owed} />
                                    </span>
                                    <span className="co-row-sub">{paymentLabel(b.transaction.paymentType)}</span>
                                </span>
                                <span className="co-state is-pending">Pending</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}

CashOutFlow.propTypes = {
    game: PropTypes.object.isRequired,
    player: PropTypes.object,
    onClose: PropTypes.func.isRequired,
};
